/**
 * Tools Marketplace API Service
 * Handles all tools-related API calls including categories, vendors, and view tracking
 */

import { apiGet, apiPost, apiPut, apiDelete } from "./api";
import type {
  ApiToolCategory,
  ApiToolVendor,
  ApiToolListResponse,
  ApiViewTrackResponse,
  ApiReviewListResponse,
  ApiMyReview,
  ApiRatingSummary,
  ToolCategory,
  ToolVendor,
  ToolListResponse,
  ToolQueryParams,
  ViewTrackResponse,
  CategoryWithToolsResponse,
  VendorWithToolsResponse,
  PricingTier,
  IntegrationType,
  ReviewListResponse,
  MyReview,
  RatingSummary,
  ReviewCreateRequest,
  ReviewUpdateRequest,
} from "@/types/tools";

// Import transformation functions
import {
  transformCategory as transformCategoryFn,
  transformVendor as transformVendorFn,
  transformToolListResponse as transformToolListResponseFn,
  transformReviewListResponse,
  transformMyReview,
  transformRatingSummary,
} from "@/types/tools";

// ============================================================================
// Tool Categories
// ============================================================================

/**
 * Get all active tool categories with tool counts
 */
export async function getToolCategories(): Promise<ToolCategory[]> {
  const categories = await apiGet<ApiToolCategory[]>("/tools/categories");
  return categories.map(transformCategoryFn);
}

/**
 * Get a category by slug with its tools
 */
export async function getCategoryWithTools(
  slug: string,
  skip = 0,
  limit = 20,
): Promise<CategoryWithToolsResponse> {
  const response = await apiGet<{
    category: {
      id: string;
      slug: string;
      name: string;
      description?: string;
      icon?: string;
    };
    tools: Array<{
      id: string;
      title: string;
      description: string;
      external_url?: string;
      topics: string[];
      difficulty: string;
      author?: string;
      thumbnail_url?: string;
      featured: boolean;
      pricing_tier?: string;
      pricing_details?: string;
      integration_type?: string;
      view_count: number;
      average_rating?: number | null;
      rating_count?: number;
      created_at: string;
      updated_at: string;
    }>;
    total: number;
  }>(`/tools/categories/${slug}`, { skip, limit });

  return {
    category: {
      id: response.category.id,
      slug: response.category.slug,
      name: response.category.name,
      description: response.category.description,
      icon: response.category.icon,
    },
    tools: response.tools.map((t) => ({
      id: t.id,
      title: t.title,
      description: t.description,
      externalUrl: t.external_url,
      topics: t.topics,
      difficulty: t.difficulty,
      author: t.author,
      thumbnailUrl: t.thumbnail_url,
      featured: t.featured,
      pricingTier: t.pricing_tier as PricingTier | undefined,
      pricingDetails: t.pricing_details,
      integrationType: t.integration_type as IntegrationType | undefined,
      viewCount: t.view_count,
      averageRating: t.average_rating ?? null,
      ratingCount: t.rating_count ?? 0,
      createdAt: t.created_at,
      updatedAt: t.updated_at,
      category: null,
      vendor: null,
      estimatedMinutes: null,
      documentationUrl: null,
      githubUrl: null,
      demoVideoUrl: null,
      screenshotUrls: null,
    })),
    total: response.total,
  };
}

// ============================================================================
// Tool Vendors
// ============================================================================

/**
 * Get all active tool vendors with tool counts
 */
export async function getToolVendors(
  verifiedOnly = false,
): Promise<ToolVendor[]> {
  const vendors = await apiGet<ApiToolVendor[]>("/tools/vendors", {
    verified_only: verifiedOnly,
  });
  return vendors.map(transformVendorFn);
}

/**
 * Get a vendor by slug with their tools
 */
export async function getVendorWithTools(
  slug: string,
  skip = 0,
  limit = 20,
): Promise<VendorWithToolsResponse> {
  const response = await apiGet<{
    vendor: {
      id: string;
      slug: string;
      name: string;
      website?: string;
      logo_url?: string;
      description?: string;
      is_verified: boolean;
    };
    tools: Array<{
      id: string;
      title: string;
      description: string;
      external_url?: string;
      topics: string[];
      difficulty: string;
      author?: string;
      thumbnail_url?: string;
      featured: boolean;
      pricing_tier?: string;
      pricing_details?: string;
      integration_type?: string;
      view_count: number;
      average_rating?: number | null;
      rating_count?: number;
      created_at: string;
      updated_at: string;
    }>;
    total: number;
  }>(`/tools/vendors/${slug}`, { skip, limit });

  return {
    vendor: {
      id: response.vendor.id,
      slug: response.vendor.slug,
      name: response.vendor.name,
      website: response.vendor.website,
      logoUrl: response.vendor.logo_url,
      description: response.vendor.description,
      isVerified: response.vendor.is_verified,
    },
    tools: response.tools.map((t) => ({
      id: t.id,
      title: t.title,
      description: t.description,
      externalUrl: t.external_url,
      topics: t.topics,
      difficulty: t.difficulty,
      author: t.author,
      thumbnailUrl: t.thumbnail_url,
      featured: t.featured,
      pricingTier: t.pricing_tier as PricingTier | undefined,
      pricingDetails: t.pricing_details,
      integrationType: t.integration_type as IntegrationType | undefined,
      viewCount: t.view_count,
      averageRating: t.average_rating ?? null,
      ratingCount: t.rating_count ?? 0,
      createdAt: t.created_at,
      updatedAt: t.updated_at,
      category: null,
      vendor: null,
      estimatedMinutes: null,
      documentationUrl: null,
      githubUrl: null,
      demoVideoUrl: null,
      screenshotUrls: null,
    })),
    total: response.total,
  };
}

// ============================================================================
// Tools Listing
// ============================================================================

/**
 * Get tools with filters and pagination
 */
export async function getTools(
  params?: ToolQueryParams,
): Promise<ToolListResponse> {
  // Transform camelCase params to snake_case for API
  const apiParams: Record<string, unknown> = {};
  if (params) {
    if (params.search) apiParams.search = params.search;
    if (params.category) apiParams.category = params.category;
    if (params.vendor) apiParams.vendor = params.vendor;
    if (params.pricingTier) apiParams.pricing_tier = params.pricingTier;
    if (params.integrationType)
      apiParams.integration_type = params.integrationType;
    if (params.difficulty) apiParams.difficulty = params.difficulty;
    if (params.featured !== undefined) apiParams.featured = params.featured;
    if (params.page !== undefined) apiParams.page = params.page;
    if (params.pageSize !== undefined) apiParams.page_size = params.pageSize;
  }

  const response = await apiGet<ApiToolListResponse>("/tools", apiParams);
  return transformToolListResponseFn(response);
}

// ============================================================================
// View Tracking
// ============================================================================

/**
 * Track a view for a tool
 */
export async function trackToolView(
  toolId: string,
): Promise<ViewTrackResponse> {
  const response = await apiPost<ApiViewTrackResponse>(
    `/tools/${toolId}/view`,
    {},
  );
  return {
    success: response.success,
    viewCount: response.view_count,
  };
}

// ============================================================================
// Tool Reviews
// ============================================================================

/**
 * Get approved reviews for a tool
 */
export async function getToolReviews(
  toolId: string,
  page = 1,
  pageSize = 10,
): Promise<ReviewListResponse> {
  const response = await apiGet<ApiReviewListResponse>(
    `/tools/${toolId}/reviews`,
    { page, page_size: pageSize },
  );
  return transformReviewListResponse(response);
}

/**
 * Get current user's review for a tool
 */
export async function getMyReview(toolId: string): Promise<MyReview | null> {
  const response = await apiGet<ApiMyReview | null>(
    `/tools/${toolId}/reviews/mine`,
  );
  if (!response) return null;
  return transformMyReview(response);
}

/**
 * Create a new review for a tool
 */
export async function createReview(
  toolId: string,
  data: ReviewCreateRequest,
): Promise<MyReview> {
  const response = await apiPost<ApiMyReview>(`/tools/${toolId}/reviews`, {
    rating: data.rating,
    review_title: data.reviewTitle,
    review_text: data.reviewText,
  });
  return transformMyReview(response);
}

/**
 * Update current user's review for a tool
 */
export async function updateMyReview(
  toolId: string,
  data: ReviewUpdateRequest,
): Promise<MyReview> {
  const response = await apiPut<ApiMyReview>(`/tools/${toolId}/reviews/mine`, {
    rating: data.rating,
    review_title: data.reviewTitle,
    review_text: data.reviewText,
  });
  return transformMyReview(response);
}

/**
 * Delete current user's review for a tool
 */
export async function deleteMyReview(toolId: string): Promise<void> {
  await apiDelete(`/tools/${toolId}/reviews/mine`);
}

/**
 * Get rating summary for a tool
 */
export async function getRatingSummary(toolId: string): Promise<RatingSummary> {
  const response = await apiGet<ApiRatingSummary>(
    `/tools/${toolId}/rating-summary`,
  );
  return transformRatingSummary(response);
}
